import { closedActionItemExpiry, } from "@repo/common";
import { collection, doc, getDoc, getDocs, query, runTransaction, Timestamp, where, writeBatch, } from "firebase/firestore";
import { auditLogsCollection } from "./auditLogs";
import { prepareForFirestore } from "./utils";
function actionItemAudit(userId, companyId, actionItemId, callSid, type, data) {
    if (!userId.trim())
        throw new Error("Action item changes require a user ID");
    return {
        userId,
        companyId,
        type,
        timestamp: new Date(),
        data: { source: "ui", actionItemId, callSid, ...data },
    };
}
export const actionItemConverter = {
    toFirestore(value) {
        return {
            ...prepareForFirestore(value),
            ...(value.closedAt !== undefined
                ? {
                    closedAt: value.closedAt instanceof Date
                        ? Timestamp.fromDate(value.closedAt)
                        : value.closedAt,
                }
                : {}),
            ...(value.expireAt !== undefined
                ? {
                    expireAt: value.expireAt instanceof Date
                        ? Timestamp.fromDate(value.expireAt)
                        : value.expireAt,
                }
                : {}),
        };
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        return {
            ...data,
            id: snapshot.id,
            createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : data.createdAt
                    ? new Date(data.createdAt)
                    : null,
            updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate()
                : data.updatedAt
                    ? new Date(data.updatedAt)
                    : null,
            closedAt: data.closedAt instanceof Timestamp ? data.closedAt.toDate() : null,
            expireAt: data.expireAt instanceof Timestamp ? data.expireAt.toDate() : null,
        };
    },
};
export function actionItemsCollection(db, companyId) {
    return collection(db, "companies", companyId, "actionItems").withConverter(actionItemConverter);
}
export function actionItemDocument(db, companyId, actionItemId) {
    return doc(db, "companies", companyId, "actionItems", actionItemId).withConverter(actionItemConverter);
}
export async function fetchActionItem(db, companyId, actionItemId) {
    const snapshot = await getDoc(actionItemDocument(db, companyId, actionItemId));
    return snapshot.exists() ? snapshot.data() : null;
}
export async function fetchActionItemsForCall(db, companyId, callSid) {
    const q = query(actionItemsCollection(db, companyId), where("callSid", "==", callSid));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data());
}
export async function createActionItem(db, companyId, data, userId) {
    const docRef = doc(actionItemsCollection(db, companyId));
    const now = new Date();
    const actionItem = {
        ...data,
        id: docRef.id,
        createdAt: now,
        updatedAt: now,
        ...(data.status === "closed"
            ? {
                closedAt: now,
                expireAt: closedActionItemExpiry(now),
            }
            : { closedAt: null, expireAt: null }),
    };
    const audit = actionItemAudit(userId, companyId, docRef.id, data.callSid, "action_item_created", { status: data.status });
    const batch = writeBatch(db);
    batch.set(docRef, actionItem);
    batch.set(doc(auditLogsCollection(db)), audit);
    await batch.commit();
    return docRef.id;
}
export async function updateActionItem(db, companyId, actionItemId, data, userId) {
    if (!userId.trim())
        throw new Error("Action item changes require a user ID");
    const docRef = actionItemDocument(db, companyId, actionItemId);
    const auditRef = doc(auditLogsCollection(db));
    await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(docRef);
        if (!snapshot.exists())
            throw new Error("Action item not found");
        const current = snapshot.data();
        const now = new Date();
        const nextStatus = data.status ?? current.status;
        const closedAt = nextStatus === "closed"
            ? current.status === "closed" && current.closedAt
                ? current.closedAt
                : now
            : null;
        transaction.update(docRef, {
            ...data,
            updatedAt: now,
            closedAt,
            expireAt: closedAt ? closedActionItemExpiry(closedAt) : null,
        });
        transaction.set(auditRef, actionItemAudit(userId, companyId, actionItemId, current.callSid, "action_item_updated", {
            fields: Object.keys(data).sort(),
            previousStatus: current.status,
            status: nextStatus,
        }));
    });
}
export async function deleteActionItem(db, companyId, actionItemId, userId) {
    if (!userId.trim())
        throw new Error("Action item changes require a user ID");
    const docRef = actionItemDocument(db, companyId, actionItemId);
    const auditRef = doc(auditLogsCollection(db));
    await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(docRef);
        if (!snapshot.exists())
            throw new Error("Action item not found");
        const current = snapshot.data();
        transaction.delete(docRef);
        transaction.set(auditRef, actionItemAudit(userId, companyId, actionItemId, current.callSid, "action_item_deleted", {
            previousStatus: current.status,
        }));
    });
}
